import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { MembershipSettingsSchemaType } from '@components/SpaceSettingsPanel/membershipSettingsSchema'
import { FadeInBox } from '@components/Transitions'
import { ErrorMessage, Paragraph, RadioCard, Stack, TextField } from '@ui'
import { TOWNS_PRICING_TERMS_LINK } from 'data/links'
import { atoms } from 'ui/styles/atoms.css'
import { shimmerClass } from 'ui/styles/globals/shimmer.css'
import {
    findDynamicPricingModule,
    findFixedPricingModule,
    PricingModuleStruct,
} from 'use-towns-client'

import { useEthInputChange } from './useEthInputChange'

export function EditPricing({
    pricingModules,
    isLoadingPricingModules,
    minimumMembershipPrice,
    isLoadingMinMembershipPrice,
    isFixed,
    isCurrentlyFree,
    isCurrentlyDynamic,
    currentMembershipCost,
}: {
    pricingModules?: PricingModuleStruct[]
    isLoadingPricingModules?: boolean
    minimumMembershipPrice?: string
    isLoadingMinMembershipPrice?: boolean
    isFixed?: boolean
    isCurrentlyFree?: boolean
    isCurrentlyDynamic?: boolean
    currentMembershipCost: string
}) {
    const { formState, setValue, watch, trigger } = useFormContext<MembershipSettingsSchemaType>()

    const formProps = useFormContext<MembershipSettingsSchemaType>()

    const [price] = watch(['membershipCost', 'membershipLimit'])

    const onCostChange = useEthInputChange(price, 'membershipCost', setValue, trigger)

    const clientPricingOption = watch('clientPricingOption')
    const membershipCost = watch('membershipCost')

    const presetRef = useRef({
        membershipCost: minimumMembershipPrice,
    })

    // remember values without altering the form

    const clientPricingOptionRef = useRef(clientPricingOption)
    clientPricingOptionRef.current = clientPricingOption

    useEffect(() => {
        if (clientPricingOptionRef.current === 'fixed') {
            presetRef.current.membershipCost = membershipCost
        }
    }, [membershipCost])

    const enabledPricingModules = useMemo(() => {
        if (pricingModules) {
            return [
                findDynamicPricingModule(pricingModules) ? ('dynamic' as const) : undefined,
                findFixedPricingModule(pricingModules) ? ('fixed' as const) : undefined,
            ].filter(Boolean)
        }
    }, [pricingModules])

    // 3.6.25
    // we're going to be updating this form to allow for swtiching between different pricing modules
    // for now, only allowing dynamic -> fixed
    // so, dynamic should only show if a space is using dynamic pricing
    const showDynamicPricing = enabledPricingModules?.includes('dynamic') && !isFixed
    // fixed can show if a space is using fixed pricing or dynamic pricing
    const showFixedPricing = enabledPricingModules?.includes('fixed')

    const showFreeOption = showFixedPricing && isCurrentlyFree === true && !isCurrentlyDynamic

    const onSelectOption = useCallback(
        (option: typeof clientPricingOption) => {
            switch (option) {
                case 'dynamic': {
                    formProps.setValue('membershipPricingType', 'dynamic')
                    formProps.setValue('membershipCost', '0.0', {
                        shouldValidate: true,
                    })
                    break
                }
                // 8.11.25
                // "free" is fixed pricing module with a price of 0
                // BUT it can have some ETH required because of the protocol fee, once free allocations have been exhausted
                // if this is a "free" space, we can set the price back to the original value of the space if we're toggling back and forth on the form
                // The "free" option only shows to the user if the space is already "free" - spaces that are not cannot switch to free, so this block only ever needs to set the price back to the original value
                // non-original values - like just setting to 0.0 - can cause contract reverts when the space is charging protocol fees
                case 'free': {
                    if (!showFreeOption) {
                        return
                    }
                    formProps.setValue('membershipPricingType', 'fixed')
                    formProps.setValue('membershipCost', currentMembershipCost, {
                        shouldValidate: true,
                    })
                    break
                }
                case 'fixed': {
                    formProps.setValue('membershipPricingType', 'fixed')
                    formProps.setValue(
                        'membershipCost',
                        presetRef.current.membershipCost ?? minimumMembershipPrice ?? '',
                        {
                            shouldValidate: false,
                        },
                    )

                    break
                }
            }
        },
        [formProps, currentMembershipCost, minimumMembershipPrice, showFreeOption],
    )

    const onSelectOptionRef = useRef<typeof onSelectOption>(onSelectOption)
    onSelectOptionRef.current = onSelectOption

    useEffect(() => {
        onSelectOptionRef.current(clientPricingOption)
    }, [clientPricingOption])

    if (isLoadingPricingModules) {
        return (
            <Stack
                padding
                gap="sm"
                rounded="sm"
                color="gray2"
                background="level2"
                className={shimmerClass}
            >
                <Stack horizontal gap="sm">
                    <Paragraph>Loading pricing modules</Paragraph>
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack gap="sm">
            {showDynamicPricing && (
                <RadioCard
                    selected={clientPricingOption === 'dynamic'}
                    name="clientPricingOption"
                    value="dynamic"
                    title="Dynamic"
                    description="Logarithmically increasing price"
                    dataTestId="membership-pricing-type-dynamic"
                    {...formProps}
                />
            )}
            {showFreeOption && (
                <RadioCard
                    name="clientPricingOption"
                    value="free"
                    title="Free"
                    description={
                        <>
                            Town is free to join.{' '}
                            <a
                                href={TOWNS_PRICING_TERMS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={atoms({
                                    textDecoration: 'underline',
                                })}
                            >
                                Terms
                            </a>{' '}
                            apply.
                        </>
                    }
                    dataTestId="membership-pricing-type-fixed-free"
                    {...formProps}
                />
            )}
            {showFixedPricing && (
                <RadioCard
                    name="clientPricingOption"
                    value="fixed"
                    title="Fixed"
                    description={
                        clientPricingOption === 'fixed'
                            ? 'Everyone pays the same price. Additional protocol fees apply.'
                            : 'Everyone pays the same price.'
                    }
                    dataTestId="membership-pricing-type-fixed"
                    {...formProps}
                >
                    {clientPricingOption === 'fixed' ? (
                        <TextField
                            autoFocus
                            background="level3"
                            autoComplete="one-time-code"
                            data-testid="fixed-town-membership-cost"
                            {...formProps.register('membershipCost')}
                            disabled={isLoadingMinMembershipPrice}
                            after="ETH"
                            border={
                                formProps.formState.errors['membershipCost']
                                    ? 'negative'
                                    : undefined
                            }
                            onChange={(e) => onCostChange(e.target.value)}
                        />
                    ) : null}
                </RadioCard>
            )}
            {formState.errors['membershipCost'] ? (
                <FadeInBox key="error">
                    <ErrorMessage errors={formState.errors} fieldName="membershipCost" />
                </FadeInBox>
            ) : formState.errors['membershipPricingType'] ? (
                <FadeInBox key="error">
                    <ErrorMessage errors={formState.errors} fieldName="membershipPricingType" />
                </FadeInBox>
            ) : null}
        </Stack>
    )
}
